/* eslint-disable no-shadow */
import { Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { MenuIcon } from '@heroicons/react/outline';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { Button, ButtonExtraLarge } from 'elements/Forms';
import Logo from '../../public/media/vendasta-yesware-logo.svg';

import EmailTracking from '../../public/media/header-icons/yesware-sales-email-tracking.svg';
import MultiChannelCampaigns from '../../public/media/header-icons/yesware-sales-multi-channel-campaigns.svg';
import EmailTemplates from '../../public/media/header-icons/yesware-sales-email-templates.svg';
import Prospector from '../../public/media/header-icons/yesware-product-prospector.svg';
import AttachmentTracking from '../../public/media/header-icons/yesware-sales-attachment-tracking.svg';
import MeetingScheduler from '../../public/media/header-icons/yesware-sales-meeting-scheduler.svg';
import ReportingAndAnalytics from '../../public/media/header-icons/yesware-sales-reporting-and-analytics.svg';
import Integrations from '../../public/media/header-icons/yesware-product-integrations.svg';

import Outlook from '../../public/media/header-icons/yesware-integrations-outlook.svg';
import Gmail from '../../public/media/header-icons/yesware-integrations-gmail.svg';

import Help from '../../public/media/header-icons/yesware-megamenu-icon-help.svg';
import ReportsAndGuides from '../../public/media/header-icons/yesware-megamenu-icon-reports.svg';
import Blog from '../../public/media/header-icons/yesware-megamenu-icon-blog.svg';
import SupportArticles from '../../public/media/header-icons/yesware-megamenu-icon-support.svg';
import LiveTraining from '../../public/media/header-icons/yesware-megamenu-icon-live.svg';
import University from '../../public/media/header-icons/yesware-megamenu-icon-university.svg';
import Podcast from '../../public/media/header-icons/yesware-megamenu-icon-podcast.svg';
import CustomerStories from '../../public/media/header-icons/yesware-megamenu-icon-customer.svg';
import IconContact from '../../public/media/header-icons/yesware-icon-contact.svg';
import IconData from '../../public/media/header-icons/yesware-icon-data.svg';
import IconPrivacy from '../../public/media/header-icons/yesware-icon-privacy.svg';

const product = [
  {
    name: 'Email Tracking',
    icon: EmailTracking,
    description: 'Get real-time open and link click notifications',
    href: '/feature/email-tracking',
  },

  {
    name: 'Multi-Channel Campaigns',
    icon: MultiChannelCampaigns,
    description: 'Build pipeline with outbound campaigns',
    href: '/feature/campaigns',
  },
  {
    name: 'Email Templates',
    icon: EmailTemplates,
    description: 'Quickly follow up with personalized emails',
    href: '/feature/email-templates',
  },
  {
    name: 'Prospector',
    icon: Prospector,
    description: 'Source new business in seconds',
    href: '/feature/prospector',
  },
  {
    name: 'Attachment Tracking',
    icon: AttachmentTracking,
    description: 'See when recipients view attachments',
    href: '/feature/attachment-tracking',
  },
  {
    name: 'Meeting Scheduler',
    icon: MeetingScheduler,
    description: 'Schedule meetings without the back-and-forth',
    href: '/feature/meeting-scheduler',
  },
  {
    name: 'Reporting & Analytics',
    icon: ReportingAndAnalytics,
    description: 'Discover the best workflows and content',
    href: '/feature/reporting-and-analytics',
  },
  {
    name: 'Integrations',
    icon: Integrations,
    description: 'Integrate with the tools you know and love',
    href: '/integrations',
  },
];

const downloads = [
  {
    name: 'Outlook Add‑On',
    icon: Outlook,
    description: 'Download Yesware for Outlook',
    href: '/platform/outlook',
  },
  {
    name: 'Gmail Chrome Extension',
    icon: Gmail,
    description: 'Download Yesware for Gmail',
    href: '/platform/gmail',
  },
];

const data = [
  {
    name: 'Prospector',
    icon: LiveTraining,
    description: 'Get leads from our database of 100+ million B2B contacts',
    href: '/feature/prospector',
  },

  {
    name: 'Privacy & Security',
    icon: IconPrivacy,
    description: 'Industry-leading security standards',
    href: '/feature/privacy-and-security',
  },
  {
    name: 'Contact Enrichment',
    icon: IconContact,
    description: 'Enrich contacts with the most accurate data',
    href: '/feature/contacts',
  },
  {
    name: 'Data Quality',
    icon: IconData,
    description: 'Data you can trust to reach target buyers',
    href: '/feature/data-quality',
  },
];

const learn = [
  {
    name: 'Support',
    icon: Help,
    description: 'Connect with our support team',
    href: 'https://docs.yesware.com/docs/',
  },
  {
    name: 'University',
    icon: University,
    description: 'Become a top performer with Yesware',
    href: '/yesware-u/',
  },
  {
    name: 'Blog',
    icon: Blog,
    description: 'Adopt the latest outreach strategies and tactics',
    href: '/blog/',
  },
  {
    name: 'Customers',
    icon: CustomerStories,
    description: 'Learn why 6,000+ teams choose Yesware',
    href: '/customers',
  },
  {
    name: 'Live Training',
    icon: LiveTraining,
    description: 'Learn product workflows and ask questions',
    href: '/product-trainings/',
  },
  {
    name: 'Reports and Guides',
    icon: ReportsAndGuides,
    description: 'Discover the latest trends and best practices',
    href: '/resources',
  },
  {
    name: 'Podcast',
    icon: Podcast,
    description: 'Hear growth stories from revenue leaders',
    href: '/podcast',
  },
  {
    name: 'About',
    icon: SupportArticles,
    description: 'Read the story and meet the team',
    href: '/about',
  },
];

const useCases = [
  {
    name: 'Sales Reps',
    href: '/personas#sales-reps',
  },
  {
    name: 'Sales Managers',
    href: '/personas#sales-managers',
  },
  {
    name: 'Sales Operations',
    href: '/personas#sales-operations',
  },
  {
    name: 'Sales Executives',
    href: '/personas#sales-executives',
  },
  {
    name: 'Marketing',
    href: '/personas#marketing',
  },
  {
    name: 'Customer Success',
    href: '/personas#customer-success',
  },
  {
    name: 'Account Management',
    href: '/personas#account-management',
  },
  {
    name: 'Outbound Sales',
    href: '/use-cases#outbound-sales',
  },
  {
    name: 'Inbound Sales',
    href: '/use-cases#inbound-sales',
  },
  {
    name: 'Account Based-Sales',
    href: '/use-cases#account-based-sales',
  },
];

const Header = () => (
  <Popover className="z-30">
    {({ open }) => (
      <>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 lg:justify-start lg:space-x-10">
          <div className="flex-shrink-0">
            <a href="/">
              <span className="sr-only">Yesware</span>
              <Logo className="mt-4 h-[50px] w-auto" />
            </a>
          </div>
          <div className="lg:hidden">
            <Popover.Button className="inline-flex items-center justify-center rounded-md bg-blue-06 p-2 text-white outline-none">
              <span className="sr-only">Open menu</span>
              <MenuIcon className="h-8 w-8" aria-hidden="true" />
            </Popover.Button>
          </div>
          <div className="hidden py-4 lg:flex lg:flex-1 lg:items-center lg:justify-between">
            <Popover.Group as="nav" className="flex space-x-4 nav-1:space-x-7">
              <Popover>
                {({ open }) => (
                  <>
                    <Popover.Button
                      className={classNames([
                        'group',
                        'inline-flex items-center rounded-md bg-white text-base font-semibold hover:text-blue-06 focus:outline-none',
                        open ? 'text-blue-06' : 'text-grey-08',
                      ])}
                    >
                      <span>Sales Engagement</span>
                      <ChevronDownIcon
                        className="ml-1 h-5 w-5 text-blue-06 transition-transform"
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      show={open}
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute inset-x-0 top-[82px] z-10 mx-auto w-screen max-w-7xl transform px-6">
                        <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                          <div className="flex divide-x">
                            <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-4">
                              {product.map((item) => (
                                <a
                                  key={item.name}
                                  href={item.href}
                                  className="-m-3 flex items-start rounded-lg p-3"
                                >
                                  <div className="ml-4">
                                    <div className="">
                                      <item.icon
                                        className="h-9 w-9"
                                        aria-hidden="true"
                                      />
                                    </div>
                                    <p className="mt-2 text-base font-bold text-gray-900">
                                      {item.name}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500">
                                      {item.description}
                                    </p>
                                  </div>
                                </a>
                              ))}
                            </div>
                            <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-1">
                              {downloads.map((item) => (
                                <a
                                  key={item.name}
                                  href={item.href}
                                  className="-m-3 flex items-start rounded-lg p-3"
                                >
                                  <div className="ml-4">
                                    <div className="">
                                      <item.icon
                                        className="h-9 w-9"
                                        aria-hidden="true"
                                      />
                                    </div>
                                    <p className="mt-2 text-base font-semibold text-gray-900">
                                      {item.name}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500">
                                      {item.description}
                                    </p>
                                  </div>
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
              <Popover>
                {({ open }) => (
                  <>
                    <Popover.Button
                      className={classNames([
                        'group',
                        'inline-flex items-center rounded-md bg-white text-base font-semibold hover:text-blue-06 focus:outline-none',
                        open ? 'text-blue-06' : 'text-grey-08',
                      ])}
                    >
                      <span>Data Enrichment</span>
                      <ChevronDownIcon
                        className="ml-1 h-5 w-5 text-blue-06 transition-transform"
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      show={open}
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute inset-x-0 -left-32 top-[82px] z-10 mx-auto w-[484px] max-w-7xl transform px-6">
                        <div className=" overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                          <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-2">
                            {data.map((item) => (
                              <a
                                key={item.name}
                                href={item.href}
                                className="-m-3 flex items-start rounded-lg p-3"
                              >
                                <div className="ml-4">
                                  <div className="">
                                    <item.icon
                                      className="h-9 w-9"
                                      aria-hidden="true"
                                    />
                                  </div>
                                  <p className="mt-2 text-base font-bold text-gray-900">
                                    {item.name}
                                  </p>
                                  <p className="mt-1 text-sm text-gray-500">
                                    {item.description}
                                  </p>
                                </div>
                              </a>
                            ))}
                          </div>
                          <div className="bg-blue-02 p-4 text-center font-bold">
                            <a
                              href="/plans-and-pricing"
                              className="text-blue-06"
                            >
                              View Data Enrichment Pricing{' '}
                              <ChevronRightIcon
                                className="ml-1 inline-block h-6 w-6 text-blue-06 transition-transform"
                                aria-hidden="true"
                              />
                            </a>
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
              <Popover>
                {({ open }) => (
                  <>
                    <Popover.Button
                      className={classNames([
                        'group',
                        'inline-flex items-center rounded-md bg-white text-base font-semibold hover:text-blue-06 focus:outline-none',
                        open ? 'text-blue-06' : 'text-grey-08',
                      ])}
                    >
                      <span>Learn</span>
                      <ChevronDownIcon
                        className="ml-1 h-5 w-5 text-blue-06 transition-transform"
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      show={open}
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute inset-x-0 top-[82px] z-10 mx-auto w-screen max-w-7xl transform px-6">
                        <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                          <div className="flex divide-x">
                            <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-4">
                              {learn.map((item) => (
                                <a
                                  key={item.name}
                                  href={item.href}
                                  className="-m-3 flex items-start rounded-lg p-3"
                                >
                                  <div className="ml-4">
                                    <div className="">
                                      <item.icon
                                        className="h-9 w-9"
                                        aria-hidden="true"
                                      />
                                    </div>
                                    <p className="mt-2 text-base font-bold text-gray-900">
                                      {item.name}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500">
                                      {item.description}
                                    </p>
                                  </div>
                                </a>
                              ))}
                            </div>
                            <div className="relative bg-white px-5 py-6 sm:p-8">
                              <p className="mt-2 text-base font-bold text-gray-900">
                                Use Cases
                              </p>

                              <div className="mt-4 grid grid-flow-col grid-rows-5 gap-4">
                                {useCases.map((item) => (
                                  <a
                                    key={item.name}
                                    href={item.href}
                                    className="whitespace-nowrap text-sm text-gray-500"
                                  >
                                    {item.name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>

              <a
                href="/plans-and-pricing"
                className="text-base font-semibold text-grey-08 hover:text-blue-06"
              >
                Pricing
              </a>

              <a
                href="/user/sign-in"
                className="whitespace-nowrap text-base font-medium font-semibold text-grey-08 hover:text-blue-06"
              >
                Sign In
              </a>
            </Popover.Group>
            <div className="flex items-center md:ml-4">
             <Button
  as="a"
  size="xl"
  href="/contact-sales"
  className="px-2 nav-1:px-6 bg-[#1976d2] text-white rounded font-bold"
>
  Contact Sales
</Button>

<ButtonExtraLarge
  as="a"
  href="/sign-up" 
   className="ml-4 px-2 nav-1:px-6 !border !border-[#1976d2] !text-[#1976d2] !bg-transparent !hover:bg-transparent rounded font-bold"
>
  Try for free
</ButtonExtraLarge>

            </div>
          </div>
        </div>

        <Transition
          show={open}
          as={Fragment}
          enter="duration-200 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            static
            className="absolute inset-x-0 top-20 origin-top-right transform shadow-sm transition lg:hidden"
          >
            <div className="bg-white ring-1 ring-black ring-opacity-5">
              <div className="px-5 pb-6 pt-5">
                <div className="mt-6">
                  <Popover.Group as="nav" className="grid gap-8">
                    <Popover>
                      {({ open }) => (
                        <>
                          <Popover.Button
                            className={classNames([
                              'group',
                              'flex w-full justify-between rounded-md bg-white text-base font-medium hover:text-blue-06 focus:outline-none',
                              open ? 'text-blue-06' : 'text-grey-08',
                            ])}
                          >
                            <span>Sales Enablement</span>
                            <ChevronDownIcon
                              className="ml-1 h-5 w-5 text-blue-06 transition-transform"
                              aria-hidden="true"
                            />
                          </Popover.Button>

                          <Transition
                            show={open}
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                          >
                            <Popover.Panel static className="">
                              <div className="divide-y">
                                <div className="grid grid-cols-1 gap-6 bg-white px-5 py-6 sm:grid-cols-2 sm:gap-8 sm:p-8">
                                  {product.map((item) => (
                                    <a
                                      key={item.name}
                                      href={item.href}
                                      className="-m-3 flex items-start rounded-lg p-3"
                                    >
                                      <div className="ml-4">
                                        <div className="">
                                          <item.icon
                                            className="h-9 w-9"
                                            aria-hidden="true"
                                          />
                                        </div>
                                        <p className="mt-2 text-base font-bold text-gray-900">
                                          {item.name}
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                          {item.description}
                                        </p>
                                      </div>
                                    </a>
                                  ))}
                                </div>
                                <div className="grid grid-cols-1 gap-6 bg-white px-5 py-6 sm:grid-cols-2 sm:gap-8 sm:p-8">
                                  {downloads.map((item) => (
                                    <a
                                      key={item.name}
                                      href={item.href}
                                      className="-m-3 flex items-start rounded-lg p-3"
                                    >
                                      <div className="ml-4">
                                        <div className="">
                                          <item.icon
                                            className="h-9 w-9"
                                            aria-hidden="true"
                                          />
                                        </div>
                                        <p className="mt-2 text-base font-semibold text-gray-900">
                                          {item.name}
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                          {item.description}
                                        </p>
                                      </div>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            </Popover.Panel>
                          </Transition>
                        </>
                      )}
                    </Popover>
                    <Popover className="relative">
                      {({ open }) => (
                        <>
                          <Popover.Button
                            className={classNames([
                              'group',
                              'flex w-full justify-between rounded-md bg-white text-base font-medium hover:text-blue-06 focus:outline-none',
                              open ? 'text-blue-06' : 'text-grey-08',
                            ])}
                          >
                            <span>Data Enrichment</span>
                            <ChevronDownIcon
                              className="ml-1 h-5 w-5 text-blue-06 transition-transform"
                              aria-hidden="true"
                            />
                          </Popover.Button>

                          <Transition
                            show={open}
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                          >
                            <Popover.Panel static className="">
                              <div className="divide-y">
                                <div className="grid grid-cols-1 gap-6 bg-white px-5 py-6 sm:grid-cols-2 sm:gap-8 sm:p-8">
                                  {data.map((item) => (
                                    <a
                                      key={item.name}
                                      href={item.href}
                                      className="-m-3 flex items-start rounded-lg p-3"
                                    >
                                      <div className="ml-4">
                                        <div className="">
                                          <item.icon
                                            className="h-9 w-9"
                                            aria-hidden="true"
                                          />
                                        </div>
                                        <p className="mt-2 text-base font-bold text-gray-900">
                                          {item.name}
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                          {item.description}
                                        </p>
                                      </div>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            </Popover.Panel>
                          </Transition>
                        </>
                      )}
                    </Popover>
                    <Popover className="relative">
                      {({ open }) => (
                        <>
                          <Popover.Button
                            className={classNames([
                              'group',
                              'flex w-full justify-between rounded-md bg-white text-base font-medium hover:text-blue-06 focus:outline-none',
                              open ? 'text-blue-06' : 'text-grey-08',
                            ])}
                          >
                            <span>Learn</span>
                            <ChevronDownIcon
                              className="ml-1 h-5 w-5 text-blue-06 transition-transform"
                              aria-hidden="true"
                            />
                          </Popover.Button>

                          <Transition
                            show={open}
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                          >
                            <Popover.Panel static className="">
                              <div className="divide-y">
                                <div className="grid grid-cols-1 gap-6 bg-white px-5 py-6 sm:grid-cols-2 sm:gap-8 sm:p-8">
                                  {learn.map((item) => (
                                    <a
                                      key={item.name}
                                      href={item.href}
                                      className="-m-3 flex items-start rounded-lg p-3"
                                    >
                                      <div className="ml-4">
                                        <div className="">
                                          <item.icon
                                            className="h-9 w-9"
                                            aria-hidden="true"
                                          />
                                        </div>
                                        <p className="mt-2 text-base font-bold text-gray-900">
                                          {item.name}
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                          {item.description}
                                        </p>
                                      </div>
                                    </a>
                                  ))}
                                </div>
                                <div className="relative bg-white px-5 py-6 sm:p-8">
                                  <p className="mt-2 text-base font-bold text-gray-900">
                                    Use Cases
                                  </p>

                                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-flow-col sm:grid-rows-5">
                                    {useCases.map((item) => (
                                      <a
                                        key={item.name}
                                        href={item.href}
                                        className="whitespace-nowrap text-sm text-gray-500"
                                      >
                                        {item.name}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </Popover.Panel>
                          </Transition>
                        </>
                      )}
                    </Popover>

                    <a
                      href="/plans-and-pricing"
                      className="text-base font-medium text-grey-08 hover:text-blue-06"
                    >
                      Pricing
                    </a>

                    <a
                      href="/user/sign-in"
                      className="whitespace-nowrap text-base font-medium text-grey-08 hover:text-blue-06"
                    >
                      Sign In
                    </a>
                  </Popover.Group>
                </div>
              </div>
              <div className="px-5 py-6">
 <Button
    as="a"
    size="xl"
    href="/contact-sales"
    className="block w-full bg-[#1976d2] text-white rounded text-center py-3 font-bold"
  >
    Contact Sales
  </Button>

  <ButtonExtraLarge
    as="a"
    href="/sign-up"
    className="block w-full mt-4 !border !border-[#1976d2] !text-[#1976d2] !bg-transparent !hover:!bg-transparent rounded text-center py-3 font-bold"
  >
    Try for free
  </ButtonExtraLarge>

              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </>
    )}
  </Popover>
);

export default Header;
